/**
 * Les prix CALCULÉS à la publication d'une gamme, côté écran (lot P2, registre B26).
 *
 * Le serveur les rend À PART du contenu (`GammeVersion.prixCalcules`, une entrée par ligne, jointe par `lineKey`) : une
 * ligne de gamme garde la forme d'un brouillon. Pour l'AFFICHAGE et les documents, on les rejoint à la ligne ici, une
 * seule fois — tout ce qui lit un prix (colonnes, sous-totaux, documents, classeur) lit alors la même ligne enrichie.
 * Cette ligne enrichie n'est JAMAIS renvoyée au serveur : l'éditeur reconstruit explicitement ses lignes.
 *
 * Vocabulaire (à ne jamais mélanger) :
 * - prix du TARIF : celui du fabricant (`publicPrice`, inchangé) ;
 * - prix d'ACHAT : ce que coûte la pièce, tarif − remises (celui de l'équivalent quand la ligne en a un) ;
 * - prix de VENTE : prix d'achat ÷ coefficient — ce que voit le client, sous le titre « Prix public HT ».
 *
 * Une clé de prix calculé ABSENTE veut dire « non calculé » (condition absente…) : jamais zéro.
 */
import type { GammeLigne, GammeNiveau, GammeVersion, PrixCalculeLigne } from '../api/client';

/** Une ligne avec ses prix calculés (absents quand rien n'est calculé, ou pour qui ne voit pas les prix). */
export interface GammeLigneVue extends GammeLigne {
  /** Coût d'achat réel : celui de l'équivalent quand la ligne en a un. */
  readonly purchasePrice?: number | null;
  /** Prix de vente : ce que voit le client. */
  readonly salePrice?: number | null;
}

export interface GammeNiveauVue extends Omit<GammeNiveau, 'lines'> {
  readonly lines: readonly GammeLigneVue[];
}

/** Les niveaux de la version, chaque ligne enrichie de ses prix calculés quand elle en a. */
export function niveauxAvecPrixCalcules(version: Pick<GammeVersion, 'contenu' | 'prixCalcules'>): GammeNiveauVue[] {
  const calcules = new Map<string, PrixCalculeLigne>((version.prixCalcules ?? []).map((p) => [p.lineKey, p]));
  if (calcules.size === 0) return version.contenu.levels.map((n) => n);
  return version.contenu.levels.map((niveau) => ({
    ...niveau,
    lines: niveau.lines.map((ligne): GammeLigneVue => {
      const p = calcules.get(ligne.lineKey);
      return p ? { ...ligne, purchasePrice: p.purchasePrice, salePrice: p.salePrice } : ligne;
    }),
  }));
}

const aUnPrix = (valeur: number | null | undefined): boolean => valeur !== null && valeur !== undefined;

/** Au moins une ligne porte un prix de VENTE calculé : la version peut montrer son prix au client. */
export function aDesPrixDeVente(niveaux: readonly GammeNiveauVue[]): boolean {
  return niveaux.some((n) => n.lines.some((l) => aUnPrix(l.salePrice)));
}

/** Au moins une ligne porte un coût d'achat réel calculé. */
export function aDesPrixDAchat(niveaux: readonly GammeNiveauVue[]): boolean {
  return niveaux.some((n) => n.lines.some((l) => aUnPrix(l.purchasePrice)));
}

/** D'où vient le prix lu sur une ligne. */
export type SourcePrix = 'TARIF' | 'VENTE' | 'ACHAT';

/** Le prix d'une ligne selon sa source ; `null` quand il n'est pas renseigné (jamais zéro inventé). */
export function prixDeLaLigne(ligne: GammeLigneVue, source: SourcePrix): number | null {
  const valeur = source === 'TARIF' ? ligne.publicPrice : source === 'VENTE' ? ligne.salePrice : ligne.purchasePrice;
  return valeur === undefined ? null : valeur;
}
